<template>
  <div
    class="text_input_holder"
    :class="{ 'text_input_holder--compact': compact }"
  >
    <div
      class="text_input"
      :class="{
        'text_input--invalid': displayInvalid,
        'text_input--input-centered': label === undefined || hideLabelOnInput,
        'text_input--has-value': valueLength > 0,
        'text_input--compact': compact,
        'text_input--disabled': disabled,
      }"
      v-bind:style="{ height: displayHeight }"
    >
      <div
        class="text_input__input_and_counter_holder"
        :class="{
          text_input__input_and_counter_holder_multiline: multiline && label,
        }"
      >
        <div
          v-if="multiline"
          ref="shadowHeight"
          class="text_input__shadow_sizer text_input__input text_input__input--multiline"
          :class="{
            'text_input__input--counter-is-active':
              !multiline && valueMaxLength,
            'text_input__input--has-value': valueLength > 0,
            'text_input__input--more-spacing-top':
              label && (isFocussed || hasValue),
          }"
        >
          {{ value }}<br />
        </div>
        <textarea
          v-if="multiline"
          :value="value"
          @change="change()"
          @input="textAreaInput($event.target.value)"
          @focus="onFocus()"
          @blur="onBlur()"
          ref="input"
          :disabled="disabled"
          class="text_input__input text_input__input--multiline"
          :class="{
            'text_input__input--counter-is-active':
              !multiline && valueMaxLength,
            'text_input__input--has-value': valueLength > 0,
            'text_input__input--disabled': disabled,
          }"
        ></textarea>
        <input
          v-if="!multiline"
          :value="value"
          :type="type"
          @change="change()"
          @input="inputValue = $event.target.value"
          @focus="onFocus()"
          @blur="onBlur()"
          :min="min"
          :max="max"
          ref="input"
          class="text_input__input"
          :disabled="disabled"
          :class="{
            'text_input__input--counter-is-active': valueMaxLength,
            'text_input__input--has-value': valueLength > 0,
            'text_input__input--numeric': numeric,
            'text_input__input--no-label': !label,
            'text_input__input--compact': compact,
            'text_input__input--disabled': disabled,
          }"
          v-bind="textInputProps"
        />
        <div
          v-if="!compact"
          class="text_input__controls"
          :class="{
            'text_input__controls--multiline': multiline,
          }"
        >
          <div
            v-if="valueMaxLength && displayValueMaxLength"
            class="text_input__counter"
            :class="{
              'text_input__counter--warning':
                maxLengthWarningLevel === 'warning',
              'text_input__counter--error': maxLengthWarningLevel === 'error',
              'text_input__counter--disabled': disabled,
            }"
          >
            {{ valueMaxLength - valueLength }}
          </div>

          <div v-if="numeric" class="text_input__spinner">
            <button
              class="text_input__spin_button text_input__spin_button--up"
              :class="{
                'text_input__spin_button--disabled': disabled,
                'text_input__spin_button--partial_disable': reachedMax,
              }"
              tabindex="-1"
              type="button"
              :disabled="disabled"
              @click="add(1)"
            >
              <font-awesome-icon :icon="['fal', 'chevron-up']" />
            </button>
            <button
              class="text_input__spin_button text_input__spin_button--down"
              :class="{
                'text_input__spin_button--disabled': disabled,
                'text_input__spin_button--partial_disable': reachedMin,
              }"
              tabindex="-1"
              type="button"
              :disabled="disabled"
              @click="add(-1)"
            >
              <font-awesome-icon :icon="['fal', 'chevron-down']" />
            </button>
          </div>

          <div v-if="isPassword" class="text_input__password_button_holder">
            <button
              class="text_input__password_button"
              type="button"
              @click="showOrHidePassword"
            >
              <font-awesome-icon
                v-if="type === 'password'"
                :icon="['fal', 'eye']"
              />
              <font-awesome-icon v-else :icon="['fal', 'eye-slash']" />
            </button>
          </div>
          <div
            v-if="showClearButton"
            class="text_input__empty_button text_input__password_button"
            :class="{ 'text_input__empty_button--disabled': disabled }"
            @click="emptyButtonClickHandler"
          >
            <font-awesome-icon :icon="['fas', 'times-circle']" />
          </div>
        </div>
      </div>
      <div
        v-if="icon"
        class="text_input__icon-holder"
        :class="{
          'text_input__icon-holder--slid-away': isFocussed || hasValue,
        }"
      >
        <font-awesome-icon
          :icon="icon"
          class="text_input__icon"
          :class="{ 'text_input__icon--disabled': disabled }"
        ></font-awesome-icon>
      </div>
      <div v-if="showLabelOnInput" class="text_input__label_holder">
        <div
          class="text_input__label"
          :class="{
            'text_input__label--tiny': isFocussed || hasValue,
            'text_input__label--input-icon-is-available': icon,
            'text_input__label--disabled': disabled,
          }"
        >
          {{ label }}
          <span class="required_asterisk" v-if="showRequiredAsterisk">*</span>
        </div>
      </div>
    </div>
    <ValidationMessage
      v-if="!disableErrorMessageBlock"
      class="text_input__validation-message"
      :displayInvalid="displayInvalid"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronUp,
  faChevronDown,
  faEye,
  faEyeSlash,
} from "@fortawesome/pro-light-svg-icons";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BaseComponent from "../../BaseComponent";
import InputBase from "./mixins/InputBase";

library.add(faChevronUp, faChevronDown, faTimesCircle, faEye, faEyeSlash);

/**
 * @group Inputs
 * The most important input. This is the basic text input, users can type in this.
 */
export default {
  mixins: [BaseComponent, InputBase],
  name: "TextInput",
  components: {
    FontAwesomeIcon,
  },
  props: {
    /**
     * @vuese
     * Label displayed in the input, question shouldn't ever be placed here.
     * This value should always indicate how the user fills in the answer.
     * Example: 06 1234 5678, would indicate the user needs to put those spaces in.
     * This functions as a placeholder.
     */
    label: {
      type: String,
    },
    // Icon from Fontawesome, you have to pass on an icon object, see FontAwesome Vue documentation for this.
    icon: {
      type: Object,
    },
    // Maximum length the use input value may have. When set, a counter is shown in the input.
    valueMaxLength: {
      type: Number,
    },
    valueMaxLengthMessage: {
      type: Function,
      default: (valueMaxLength) =>
        `Value has more than ${valueMaxLength} characters`,
    },
    // Determines if the character counter should be shown in the input field
    displayValueMaxLength: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
    },
    requiredMessage: {
      type: String,
      default: "This input field is required.",
    },
    /**
     * @vuese
     * The type of validation the input should use.
     */
    validationType: {
      // `'email'`
      type: String,
    },
    validationTypeEmailMessage: {
      type: String,
      default: "Enter a valid email address",
    },
    // Regex for custom validation.
    customValidationRegex: {
      type: String,
    },
    // Message associated with the custom validation regex.
    customValidationMessage: {
      type: String,
    },
    // Flag to allow control of error state. If false, component will apply internal validation logic.
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    // Flag to tell this component should show error state.
    externalValidationError: {
      type: Boolean,
    },
    // Error message that gets shown when externalValidationError is true.
    externalValidationErrorMessage: {
      type: String,
    },
    // Flag to disable the validation error message text entirely while preserving other error related styling.
    disableErrorMessageBlock: {
      type: Boolean,
      default: false,
    },
    // Makes the input multiline and use a textarea.
    multiline: {
      type: Boolean,
      default: false,
    },
    // Only allows numbers and show spin button
    numeric: {
      type: Boolean,
      default: false,
    },
    // Shows cross button on which users can click to empty the input
    showClearButton: {
      type: Boolean,
      default: false,
    },
    // Minimum Value for nummeric inputs
    min: {
      type: Number,
    },
    minMessage: {
      type: Function,
      default: (min) => `This input has to be higher than ${min}`,
    },
    // Maximum Value for nummeric inputs
    max: {
      type: Number,
    },
    maxMessage: {
      type: Function,
      default: (max) => `This input has to be lower than ${max}`,
    },
    // HTML type of actual input
    inputType: {
      type: String,
      default: "text",
    },
    // Applies styling for in-line purposes
    compact: {
      type: Boolean,
      default: false,
    },
    hideLabelOnInput: {
      type: Boolean,
      default: false,
    },
    showRequiredAsterisk: {
      type: Boolean,
      default: false,
    },
    isPassword: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      someData: 0,
      height: 72,
      isFocussed: false,
      isTouched: false,
      validationMessage: "",
      validationRules: {
        email: {
          // eslint-disable-next-line max-len, no-control-regex
          regex:
            // eslint-disable-next-line no-control-regex
            /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
          // eslint-disable-next-line max-len
          message: this.validationTypeEmailMessage,
        },
      },
      type: this.isPassword ? "password" : "text",
      reachedMax: false,
      reachedMin: false,
    };
  },
  computed: {
    displayHeight() {
      let height = 56;
      if (this.multiline) {
        if (this.height !== "auto") {
          height = `${this.height + 2}px`;
        } else {
          ({ height } = this);
        }
      }
      return height;
    },
    displayInvalid() {
      if (this.enableExternalValidation) return this.externalValidationError;

      return (
        (this.isShowingValidationMessage && !this.isValid) ||
        (this.isTouched && !this.isValid)
      );
    },
    valueLength() {
      let valueLength = 0;
      if (this.inputValue) valueLength = this.inputValue.length;
      if (this.numeric) {
        if (
          this.inputValue !== "" &&
          this.inputValue !== null &&
          this.inputValue !== undefined
        ) {
          valueLength = this.inputValue.toString().length;
        } else {
          valueLength = 0;
        }
      }
      return valueLength;
    },
    hasValue() {
      let hasValue = true;
      if (
        this.inputValue === "" ||
        this.inputValue === undefined ||
        this.inputValue === null
      ) {
        hasValue = false;
      }
      return hasValue;
    },
    isChanged() {
      return this.inputValue !== "";
    },
    maxLengthWarningLevel() {
      let maxLengthWarningLevel;
      const percentageOfCharactersUsed =
        1 - this.valueLength / this.valueMaxLength;
      if (percentageOfCharactersUsed < 0.15) maxLengthWarningLevel = "warning";
      if (percentageOfCharactersUsed < 0.1) maxLengthWarningLevel = "error";
      return maxLengthWarningLevel;
    },
    isValid() {
      if (this.enableExternalValidation) return this.externalValidationError;

      let valid = true;
      if (this.required && this.valueLength === 0) {
        // Check on Required
        valid = false;
        this.setValidationMessage(this.requiredMessage);
      } else if (
        this.valueMaxLength &&
        this.valueLength > this.valueMaxLength
      ) {
        // Check if value isn't over maxlength
        valid = false;
        this.setValidationMessage(
          this.valueMaxLengthMessage(this.valueMaxLength)
        );
      } else if (
        this.hasValue &&
        this.max !== undefined &&
        this.numeric &&
        this.value > this.max
      ) {
        // Checks if a numeric value is on range (max).
        // Check max value
        valid = false;
        this.setReachedNumericValue("Max", true);
        this.setValidationMessage(this.maxMessage(this.max));
      } else if (
        this.hasValue &&
        this.max !== undefined &&
        this.numeric &&
        this.value >= this.max
      ) {
        // Checks if a numeric value git max possible value.
        this.setReachedNumericValue("Max", true);
      } else if (
        this.hasValue &&
        this.min !== undefined &&
        this.numeric &&
        this.value < this.min
      ) {
        // Checks if a numeric value is on range (min).
        // Check min value
        valid = false;
        this.setReachedNumericValue("Min", true);
        this.setValidationMessage(this.minMessage(this.min));
      } else if (
        this.hasValue &&
        this.min !== undefined &&
        this.numeric &&
        this.value <= this.min
      ) {
        // Check if a numeric value git min possible value.
        this.setReachedNumericValue("Min", true);
      } else {
        // Reset values.
        this.setReachedNumericValue("Min", false);
        this.setReachedNumericValue("Max", false);
      }

      // Check for a user passed custom Regex
      if (
        this.customValidationRegex &&
        new RegExp(this.customValidationRegex).exec(this.inputValue) === null
      ) {
        valid = false;
        this.setValidationMessage(this.customValidationMessage);
      }
      // Check for one of the default Regexes included in this component
      if (
        !this.customValidationRegex &&
        this.validationType &&
        this.validationRules[this.validationType] &&
        this.validationRules[this.validationType].regex.exec(
          this.inputValue
        ) === null
      ) {
        valid = false;
        this.setValidationMessage(
          this.validationRules[this.validationType].message
        );
      }
      return valid;
    },
    inputmode() {
      let inputmode = "text";
      if (this.inputType === "email") inputmode = "email";
      if (this.inputType === "search") inputmode = "search";
      if (this.numeric) inputmode = "numeric";
      return inputmode;
    },
    textInputProps() {
      const textInputProps = {};
      if (this.inputType) {
        textInputProps.type = this.inputType;
      }
      if (this.numeric) {
        textInputProps.inputPattern = "[0-9]*";
        textInputProps.type = "number";
      }
      textInputProps.inputmode = this.inputmode;
      return textInputProps;
    },
    showLabelOnInput() {
      return !(this.hideLabelOnInput && (this.isFocussed || this.hasValue));
    },
  },
  methods: {
    // @vuese
    // Internal event that emits when the input is blurred.
    onBlur() {
      this.isFocussed = false;
      this.isTouched = true;
      this.$emit("blur", this.inputValue);
    },
    // @vuese
    // Internal event that emits when the input gets focussed on.
    onFocus() {
      this.isFocussed = true;
      this.$emit("focus");
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        this.calculateHeight();
      });
    },
    // @vuese
    // Sets validation message
    setValidationMessage(message) {
      this.validationMessage = message;
    },
    calculateHeight() {
      // Sets the height to the input scrollheight
      if (!this.multiline) return;
      let height = this.$refs.shadowHeight.offsetHeight + 10;
      if (height < 56 || !this.inputValue) height = 54;
      this.height = height;
    },
    // @vuese
    // When input is numeric this adds @value to the current user input value.
    add(value) {
      let currentNumber = this.inputValue || 0;
      if (currentNumber === "") currentNumber = 0;
      this.inputValue = parseInt(currentNumber, 10) + value;
      this.isShowingValidationMessage = true;
    },
    // @vuese
    // Focusses the input, handy on first page loads.
    focus() {
      this.$refs.input.focus();
    },
    // @vuese
    // When the input changed
    change() {
      this.$emit("change", this.inputValue, this);
    },
    emptyButtonClickHandler() {
      if (this.disabled) return;

      this.inputValue = "";
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        this.$emit("emptyButtonClicked");
      });
    },
    blur() {
      this.$refs.input.blur();
    },
    textAreaInput(value) {
      this.inputValue = value;
      if (typeof window === "undefined") return;
      window.requestAnimationFrame(() => {
        this.calculateHeight();
      });
    },
    showOrHidePassword() {
      if (this.type === "text") {
        this.type = "password";
      } else {
        this.type = "text";
      }
    },
    setReachedNumericValue(target, value) {
      const targetProp = `reached${target}`;
      this[targetProp] = value;
    },
  },
  watch: {
    inputValue() {
      if (this.multiline) this.calculateHeight();
    },
    value() {
      if (this.multiline) this.calculateHeight();
    },
  },
  mounted() {
    if (this.multiline) this.calculateHeight();
  },
};
</script>

<!-- Add 'scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../../../style_variables/style_variables.scss";
$text_input_padding_horizontal: 14px;
$compact_height: 38px;

.text_input_holder {
  width: 100%;
  max-width: $input_max_width;

  &.text_input_holder--compact {
    height: $compact_height;
  }
}
.text_input {
  display: inline-flex;
  width: 100%;
  position: relative;
  background-color: $white;
  border: 1px solid $grey_mischka;
  border-radius: 4px;
  height: 56px;
  overflow: hidden;
  &:hover,
  &:focus {
    border: 1px solid $grey_bombay;
  }
  &.text_input--invalid {
    border-color: $red;
    transition: all 0.3s;

    .text_input__label {
      color: $red;
    }
  }
  &.text_input--input-centered {
    .text_input__input {
      padding-top: 0px;
    }
    .text_input__input--multiline {
      padding-top: 6px;
      padding-bottom: 4px;
    }
  }
  &.text_input--has-value {
    .text_input__input--multiline {
      padding-bottom: 4px;
    }
  }
  &.text_input--compact {
    height: $compact_height;
  }
  &.text_input--disabled {
    background-color: $grey-bg;
    border-color: $grey_mischka;
  }
}
.text_input__input_and_counter_holder {
  position: absolute;
  z-index: 2;
  display: flex;
  width: 100%;
  height: 100%;
}
.text_input__input_and_counter_holder_multiline {
  padding-top: 22px;
}
.text_input__input {
  @extend %body1_style;
  width: 100%;
  height: 100%;
  padding: 0px $text_input_padding_horizontal;
  padding-top: 16px;
  border: 0px;
  background: transparent;
  opacity: 1;
  outline: 0;
  &.text_input__input--has-value {
    color: $grey_mineshaft;
  }
  &.text_input__input--multiline {
    padding-top: 0px;
    padding-bottom: 4px;
    resize: none;
  }
  &.text_input__input--more-spacing-top {
    padding-top: 22px;
  }
  &.text_input__input--no-label {
    padding-top: 0px;
  }
  &.text_input__input--compact {
    height: $compact_height;
  }
  &.text_input__input--disabled {
    color: $grey_mischka;
    -webkit-text-fill-color: $grey_mischka; //Safari
  }
}
.text_input__controls {
  display: flex;
  padding-right: 14px;
  &.text_input__controls--multiline {
    height: 56px;
    position: absolute;
    right: 0px;
    bottom: 0px;
    align-items: center;
  }
}
.text_input__shadow_sizer {
  width: auto;
  height: auto;
  word-break: break-all;
  display: inline-block;
  position: absolute;
  pointer-events: none;
  opacity: 0;
  white-space: pre-line;
}

.text_input__counter {
  @extend %body1_style;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 100%;
  padding-right: $text_input_padding_horizontal;
  text-align: right;
  color: $grey_french;
  &.text_input__counter--warning {
    color: $orange;
  }
  &.text_input__counter--error {
    color: $red;
  }
  &.text_input__counter--disabled {
    color: $grey_mischka;
  }
}
.text_input__spinner {
  width: 24px;
  height: 56px;
  padding-right: 14px;
}
.text_input__spin_button {
  width: 100%;
  height: 50%;
  background: transparent;
  border: 0px;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: $black;

  &:hover {
    cursor: pointer;
    color: $blue;
  }

  &.text_input__spin_button--disabled {
    color: $grey_mischka;
    &:hover {
      cursor: default;
      color: $grey_mischka;
    }
  }

  &.text_input__spin_button--partial_disable {
    pointer-events: none;
    color: $grey_mischka;
  }
}
.text_input__spin_button--up {
  padding-top: 10px;
}
.text_input__spin_button--down {
  padding-bottom: 10px;
}

.text_input__input--counter-is-active {
  width: calc(100% - 30px);
}
.text_input__icon-holder {
  display: flex;
  width: 28px;
  height: 100%;
  align-items: center;
  padding: 0 0 0 $text_input_padding_horizontal;
  transition: transform 0.21s cubic-bezier(0.4, 0, 0.2, 1);
  &.text_input__icon-holder--slid-away {
    transform: translateX(-28px);
  }
}
.text_input__icon {
  color: $grey_french;
  &.text_input__icon--disabled {
    color: $grey_mischka;
  }
}
.text_input__empty_button {
  height: 100%;
  width: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey_french;

  &:hover {
    color: $grey_bombay;
    cursor: pointer;
  }
}
.text_input__password_button {
  width: 100%;
  height: 50%;
  background: transparent;
  border: 0px;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: $grey_bombay;
  &:hover {
    cursor: pointer;
    color: $grey_mineshaft;
  }
}
.text_input__password_button_holder {
  display: flex;
  align-items: center;
}
.text_input__empty_button--disabled {
  color: $grey_mischka;

  &:hover {
    color: $grey_mischka;
    cursor: default;
  }
}
.text_input__label_holder {
  width: 100%;
  height: 100%;
  position: relative;
  padding: 0 $text_input_padding_horizontal;
}
.text_input__label {
  @extend %body1_style;
  color: $grey_bombay;
  position: absolute;
  height: 28px;
  transform: translate(0, 27px) translateY(-50%);
  transform-origin: 0%;
  will-change: color, transform;
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &.text_input__label--input-icon-is-available {
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
      color 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &.text_input__label--tiny {
    transform: translateY(16px) translateY(-50%) scale(0.75);
    color: $blue;
    &.text_input__label--input-icon-is-available {
      transform: translateX(-28px) translateY(16px) translateY(-50%) scale(0.75);
    }
  }
  &.text_input__label--disabled {
    color: $grey_mischka;
  }
}
// for safari only
@media not all and (min-resolution: 0.001dpcm) {
  @supports (-webkit-appearance: none) {
    .text_input__label {
      will-change: auto;
    }
  }
}
.text_input__validation-message {
  margin-left: 13px;
}
// Autocomplete colour fix for Webkit Browsers:
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus {
  border: 1px solid transparent;
  -webkit-text-fill-color: $grey_mineshaft;
  transition: background-color 60000s ease-in-out 60000s;
}
// Hide default Arrows in Firefox and WebKit
input[type="number"] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.required_asterisk {
  color: $red;
}
</style>
